import React, { useCallback, useState, useMemo, useRef, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Check, ChevronLeft, ChevronRight, RefreshCw, ChevronDown, ThumbsUp, ThumbsDown, Pencil } from 'lucide-react';
import { ModelDropdown } from '../ModelSelector';
import { AI_MODELS } from '../../../constants/models';
import { submitMessageFeedback } from '../../../api/chats';
import FeedbackForm from './FeedbackForm';

import styles from './styles/MessageMenu.module.css';

const ICON_SIZE = 15;

const MessageMenu = ({
    message,
    siblings = [],
    onNavigate = () => {},
    handleRegenerateMessage = null,
    parentMessage = null,
    handleIsEditing = null,
}) => {
    const [isCopied, setIsCopied] = useState(false);
    const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState('top');
    const [feedbackState, setFeedbackState] = useState(null); // 'GOOD', 'BAD', or null
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    const modelId = useMemo(() => message.metadata?.model, [message]);

    // Handle click outside
    useEffect(() => {
        if (!isModelSelectorOpen) return;

        const handleClickOutside = event => {
            if (
                buttonRef.current?.contains(event.target) ||
                dropdownRef.current?.contains(event.target)
            ) {
                return;
            }
            setIsModelSelectorOpen(false);
        };

        // Disable scroll and add overlay
        document.body.style.overflow = 'hidden';
        const overlay = document.createElement('div');
        overlay.className = styles.overlay;
        document.body.appendChild(overlay);

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = '';
            if (overlay.parentNode) {
                overlay.parentNode.removeChild(overlay);
            }
        };
    }, [isModelSelectorOpen]);

    useEffect(() => {
        if (!isModelSelectorOpen || !buttonRef.current) return;

        const buttonRect = buttonRef.current.getBoundingClientRect();
        const spaceAbove = buttonRect.top;
        const spaceBelow = window.innerHeight - buttonRect.bottom;

        const newPosition = spaceBelow > spaceAbove ? 'bottom' : 'top';
        setDropdownPosition(newPosition);
    }, [isModelSelectorOpen]);

    const availableModels = useMemo(() => {
        const hasImages = parentMessage?.attachments?.some(file => file.type.startsWith('image/'));
        let models = Object.values(AI_MODELS).flatMap(provider => provider.models);
        if (hasImages) {
            models = models.filter(model => model.isImageSupported);
        }
        return models.map(model => model.id);
    }, [parentMessage]);

    const renderModelDropdown = () => {
        if (!isModelSelectorOpen || !buttonRef.current) return null;

        const buttonRect = buttonRef.current.getBoundingClientRect();

        return createPortal(
            <div 
                ref={dropdownRef} 
                className={styles.modelSelectorWrapper}
                style={{
                    position: 'fixed',
                    top: buttonRect.top + window.scrollY,
                    left: buttonRect.left + window.scrollX,
                    width: buttonRect.width,
                    height: buttonRect.height,
                }}
            >
                <ModelDropdown
                    selectedModel={modelId}
                    onModelSelect={newModel => {
                        if (handleRegenerateMessage) {
                            handleRegenerateMessage({ message, newModel });
                        }
                        setIsModelSelectorOpen(false);
                    }}
                    position={dropdownPosition}
                    isRegenerate={true}
                    availableModels={availableModels}
                />
            </div>,
            document.body
        );
    };

    const handleCopyMessage = useCallback(() => {
        // Create a temporary textarea element to handle copying
        const textarea = document.createElement('textarea');
        textarea.value = message.text;
        textarea.style.position = 'fixed';  // Make it invisible
        textarea.style.opacity = 0;
        document.body.appendChild(textarea);
        textarea.select();

        try {
            // Use execCommand which works better on iOS Safari
            const successful = document.execCommand('copy');
            if (!successful) {
                // Fallback to clipboard API if execCommand fails
                navigator.clipboard.writeText(message.text);
            }
            setIsCopied(true);
        } catch (err) {
            // Last resort fallback
            navigator.clipboard.writeText(message.text);
            setIsCopied(true);
        } finally {
            document.body.removeChild(textarea);
            setTimeout(() => setIsCopied(false), 2000);
        }
    }, [message]);

    const handleFeedback = useCallback(async type => {
        if (feedbackState === type) return;
        try {
            setFeedbackState(type);
            submitMessageFeedback(message.id, { feedbackType: type.toUpperCase() });

            // If user clicked "bad", show the feedback form
            if (type === 'BAD') {
                setShowFeedbackForm(true);
            }
        } catch (error) {
            setFeedbackState(null);
        }
    }, [message, feedbackState]);

    const handleCloseFeedbackForm = useCallback(() => {
        setShowFeedbackForm(false);
    }, []);

    const currentIndex = useMemo(() =>
        siblings.findIndex(msg => msg.id === message.id)
    , [siblings, message]);

    const hasSiblings = useMemo(() => siblings.length > 1 , [siblings]);

    const renderNavigateButtons = () => {
        if (!hasSiblings) return null;

        return (
            <div className={styles.navigateButtons}>
                <button
                    onClick={() => onNavigate('prev')}
                    className={styles.menuButton}
                    disabled={currentIndex === 0}
                >
                    <ChevronLeft size={ICON_SIZE} />
                </button>
                <p>{currentIndex + 1}/{siblings.length}</p>
                <button
                    onClick={() => onNavigate('next')}
                    className={styles.menuButton}
                    disabled={currentIndex === siblings.length - 1}
                >
                    <ChevronRight size={ICON_SIZE} />
                </button>
            </div>
        );
    };

    const getCurrentModelLabel = useCallback(modelId => {
        for (const provider of Object.values(AI_MODELS)) {
            const model = provider.models.find(m => m.id === modelId);
            if (model) return model.label;
        }
        return modelId;
    }, []);

    if (message.isTyping || message.status) {
        return null;
    }

    if (message.sender === 'user') {
        return (
            <div className={`${styles.messageMenu} ${styles.userMessageMenu}`}>
                <button
                    className={styles.menuButton}
                    onClick={handleCopyMessage}
                    aria-label="Copy message"
                >
                    {isCopied ? <Check size={ICON_SIZE} /> : <Copy size={ICON_SIZE} />}
                    <span className={styles.tooltip}>Copy</span>
                </button>
                {handleIsEditing && (
                    <button
                        className={styles.menuButton}
                        onClick={handleIsEditing}
                        aria-label="Edit message"
                    >
                        <Pencil size={ICON_SIZE} />
                        <span className={styles.tooltip}>Edit</span>
                    </button>
                )}
                {renderNavigateButtons()}
            </div>
        );
    }

    if (message.sender === 'assistant') {
        return (
            <div className={styles.messageMenuContainer}>
                <div className={styles.messageMenu}>
                    {renderNavigateButtons()}
                    <button
                        className={styles.menuButton}
                        onClick={handleCopyMessage}
                        aria-label="Copy message"
                    >
                        {isCopied ? <Check size={ICON_SIZE} /> : <Copy size={ICON_SIZE} />}
                        <span className={styles.tooltip}>Copy</span>
                    </button>

                    {(feedbackState === null || feedbackState === 'GOOD') && (
                        <button
                            className={`${styles.menuButton} ${feedbackState === 'GOOD' ? styles.feedbackActive : ''}`}
                            onClick={() => handleFeedback('GOOD')}
                            aria-label="God Response"
                            disabled={feedbackState === 'GOOD'}
                        >
                            <ThumbsUp size={ICON_SIZE} />
                            <span className={styles.tooltip}>God Response</span>
                        </button>
                    )}
                    {(feedbackState === null || feedbackState === 'BAD') && (
                        <button
                            className={`${styles.menuButton} ${feedbackState === 'BAD' ? styles.feedbackActive : ''}`}
                            onClick={() => handleFeedback('BAD')}
                            aria-label="Bad Response"
                            disabled={feedbackState === 'BAD'}
                        >
                            <ThumbsDown size={ICON_SIZE} />
                            <span className={styles.tooltip}>Bad Response</span>
                        </button>
                    )}

                    <div className={styles.regenerateContainer}>
                        <button
                            ref={buttonRef}
                            className={`${styles.menuButton} ${styles.regenerateButton}`}
                            onClick={() => setIsModelSelectorOpen(!isModelSelectorOpen)}
                            aria-label="Switch model"
                        >
                            <div className={styles.regenerateIcon}>
                                <RefreshCw size={ICON_SIZE} />
                                {modelId && <span className={`${styles.modelLabel} ${isModelSelectorOpen ? styles.modelLabelActive : ''}`}>
                                    {getCurrentModelLabel(modelId)}
                                </span>}
                                <ChevronDown size={13} />
                            </div>
                            <span className={styles.tooltip}>Switch model</span>
                        </button>
                        {renderModelDropdown()}
                    </div>
                </div>

                {showFeedbackForm && (
                    <FeedbackForm messageId={message.id} onClose={handleCloseFeedbackForm} />
                )}
            </div>
        );
    }

    if (message.sender === 'agent') {
        return (
            <div className={styles.messageMenuContainer}>
                <div className={styles.messageMenu}>
                    {renderNavigateButtons()}
                    <button
                        className={styles.menuButton}
                        onClick={handleCopyMessage}
                        aria-label="Copy message"
                    >
                        {isCopied ? <Check size={ICON_SIZE} /> : <Copy size={ICON_SIZE} />}
                        <span className={styles.tooltip}>Copy</span>
                    </button>
                </div>
            </div>
        );
    }

    return null;
};

export default memo(MessageMenu);
